/**
 * Carga a mano, sin endpoint propio.
 *
 * Las pantallas que dejan tipear filas —el inventario inicial, el stock de
 * ZF— arman con lo tipeado el mismo CSV que subiría el operario y lo mandan
 * por el importador que ya existe. Así la carga manual pasa por la misma
 * previsualización, las mismas validaciones y la misma confirmación
 * transaccional, sin una segunda ruta que valide parecido pero no igual.
 */

export type ColumnaManual = {
  /** El encabezado que se escribe en el CSV: tiene que ser uno que el importador entienda. */
  clave: string;
  rotulo: string;
  ancho: number;
  ejemplo: string;
};

export type FilaManual = Record<string, string>;

export const filaVacia = (columnas: readonly ColumnaManual[]): FilaManual =>
  Object.fromEntries(columnas.map((col) => [col.clave, '']));

export const filasVacias = (columnas: readonly ColumnaManual[], cuantas = 3): FilaManual[] =>
  Array.from({ length: cuantas }, () => filaVacia(columnas));

export const tieneAlgo = (f: FilaManual): boolean => Object.values(f).some((v) => v.trim() !== '');

/**
 * Las filas con algo cargado, como texto CSV. Las vacías se descartan: la
 * grilla arranca con renglones en blanco y nadie los va a borrar antes de
 * confirmar.
 *
 * Los campos con `;`, comillas o saltos se citan, que es lo que el lector del
 * backend sabe deshacer. Va separado de `csvDeFilas` porque `File` no existe
 * fuera del navegador, y esto es lo único que hay que probar.
 */
export function textoDeFilas(columnas: readonly ColumnaManual[], filas: FilaManual[]): string {
  const esc = (v: string) => (/[";\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const linea = (celda: (col: ColumnaManual) => string) => columnas.map(celda).join(';');
  return [
    linea((col) => col.clave),
    ...filas.filter(tieneAlgo).map((f) => linea((col) => esc((f[col.clave] ?? '').trim()))),
  ].join('\r\n');
}

/**
 * El archivo que se sube. El BOM va adelante porque el importador lo espera
 * igual que en un archivo guardado desde Excel.
 */
export function csvDeFilas(columnas: readonly ColumnaManual[], filas: FilaManual[], nombre: string): File {
  return new File(['﻿' + textoDeFilas(columnas, filas)], nombre, { type: 'text/csv' });
}
